import { FC, useEffect, useRef } from 'react';
import { ConsentObject } from '../../utils/cookieConsent';
import styles from './styles.module.scss';

interface CookieModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (analytics: boolean, marketing: boolean, functional: boolean) => void;
  onReject: () => void;
  onAccept: () => void;
  currentConsent: ConsentObject | null;
}

export const CookieModal: FC<CookieModalProps> = ({
  isOpen,
  onClose,
  onSave,
  onReject,
  onAccept,
  currentConsent,
}) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const analyticsRef = useRef<HTMLInputElement>(null);
  const marketingRef = useRef<HTMLInputElement>(null);
  const functionalRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen && modalRef.current) {
      modalRef.current.focus();
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) onClose();
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleSave = () => {
    onSave(
      analyticsRef.current?.checked || false,
      marketingRef.current?.checked || false,
      functionalRef.current?.checked || false,
    );
  };

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <section
        ref={modalRef}
        className={styles.modal}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="cookie-modal-title"
        tabIndex={-1}
      >
        <div className={styles.modalHeader}>
          <h2 id="cookie-modal-title">Cookie Preferences</h2>
          <button type="button" onClick={onClose} className={styles.closeBtn} aria-label="Close">
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className={styles.modalBody}>
          <p>
            You can manage your cookie preferences below. Essential cookies are always enabled
            because they are necessary for the website to function.
          </p>

          <div className={styles.categoryItem}>
            <div className={styles.categoryHeader}>
              <h3>Essential</h3>
              <label className={styles.toggle}>
                <input type="checkbox" checked disabled className={styles.toggleInput} />
                <span className={styles.toggleSlider}></span>
              </label>
            </div>
            <p>Necessary for website functionality, security and form submission.</p>
          </div>

          <div className={styles.categoryItem}>
            <div className={styles.categoryHeader}>
              <h3>Analytics</h3>
              <label className={styles.toggle}>
                <input
                  type="checkbox"
                  ref={analyticsRef}
                  defaultChecked={currentConsent?.analytics ?? false}
                  className={styles.toggleInput}
                />
                <span className={styles.toggleSlider}></span>
              </label>
            </div>
            <p>Helps us understand how visitors interact with the website.</p>
          </div>

          <div className={styles.categoryItem}>
            <div className={styles.categoryHeader}>
              <h3>Marketing</h3>
              <label className={styles.toggle}>
                <input
                  type="checkbox"
                  ref={marketingRef}
                  defaultChecked={currentConsent?.marketing ?? false}
                  className={styles.toggleInput}
                />
                <span className={styles.toggleSlider}></span>
              </label>
            </div>
            <p>Used to measure marketing campaigns and advertising effectiveness.</p>
          </div>

          <div className={styles.categoryItem}>
            <div className={styles.categoryHeader}>
              <h3>Functional</h3>
              <label className={styles.toggle}>
                <input
                  type="checkbox"
                  ref={functionalRef}
                  defaultChecked={currentConsent?.functional ?? false}
                  className={styles.toggleInput}
                />
                <span className={styles.toggleSlider}></span>
              </label>
            </div>
            <p>Stores user preferences and improves website usability.</p>
          </div>
        </div>

        <div className={styles.modalFooter}>
          <button
            type="button"
            onClick={onReject}
            className={`${styles.btn} ${styles.btnSecondary}`}
          >
            Reject All
          </button>
          <div className={styles.footerActions}>
            <button
              type="button"
              onClick={handleSave}
              className={`${styles.btn} ${styles.btnSecondary}`}
            >
              Save Preferences
            </button>
            <button
              type="button"
              onClick={onAccept}
              className={`${styles.btn} ${styles.btnPrimary}`}
            >
              Accept All
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};
